import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { FunctionsHttpError } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { UsuarioInput } from '../types/usuario';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';

export function useUsuarios() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  // Query para listar usuários
  // IMPORTANTE: Esta query implementa controle de acesso baseado no tipo do usuário
  // - Usuários admin podem ver todos os registros
  // - Outros usuários só podem ver os usuários que eles mesmos cadastraram (id_revenda)
  const { data: usuarios, isLoading: listLoading } = useQuery({
    queryKey: ['usuarios', user?.tipo, user?.id], // Inclui tipo e id para recarregar quando mudar o usuário
    queryFn: async () => {
      let query = supabase
        .from('usuarios')
        .select('*')
        .order('nome', { ascending: true });

      // CRÍTICO: Filtro de segurança - Não remover
      // Restringe acesso apenas aos usuários vinculados ao id_revenda do usuário atual
      if (user?.tipo !== 'admin') {
        query = query.eq('id_revenda', user?.id);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data || [];
    },
    enabled: !!user, // Só executa quando houver usuário logado
    // Adicionar configurações de revalidação
    refetchOnWindowFocus: true,      // Recarrega quando a janela ganha foco
    refetchOnMount: true,            // Recarrega quando o componente é montado
    refetchInterval: 5 * 60 * 1000,  // Recarrega a cada 5 minutos (opcional)
    staleTime: 60 * 1000,            // Considera os dados obsoletos após 1 minuto
  });

  // Query para buscar usuário específico
  // SEGURANÇA: Mantém a mesma lógica de controle de acesso da listagem
  // Impede que usuários não-admin acessem dados de usuários que não cadastraram
  const useUsuario = (id: string) => {
    return useQuery({
      queryKey: ['usuarios', id],
      queryFn: async () => {
        // Inicia a query base
        let query = supabase
          .from('usuarios')
          .select('*')
          .eq('id', id);

        // CRÍTICO: Filtro de segurança - Não remover
        // Aplica o filtro de id_revenda apenas para não-admin e quando houver um id
        if (user?.tipo !== 'admin' && user?.id) {
          query = query.eq('id_revenda', user.id);
        }

        const { data, error } = await query.single();

        if (error) throw error;
        return data;
      },
      enabled: !!id && !!user,
    });
  };

  // Mutation para criar usuário
  // A criação acontece na Edge Function `criar-usuario` (supabase/functions), com a
  // service role. Ela confere se quem chama é admin ou revenda e decide no servidor
  // tipo, revenda e saldo inicial. Não há mais `signUp` no navegador: o autocadastro
  // público fica desligado no Supabase Auth.
  const { mutateAsync: criarUsuario } = useMutation({
    mutationFn: async (usuario: UsuarioInput) => {
      const { error } = await supabase.functions.invoke('criar-usuario', {
        body: usuario,
      });

      if (error) {
        // Em erro HTTP, a mensagem útil vem no corpo da resposta da função
        let mensagem = error.message;
        if (error instanceof FunctionsHttpError) {
          const corpo = await error.context.json().catch(() => null);
          if (corpo?.error) mensagem = corpo.error;
        }
        throw new Error(mensagem);
      }

      return true;
    },
    onSuccess: () => {
      // Invalidar todas as queries relacionadas a usuários
      queryClient.invalidateQueries({ queryKey: ['usuarios'] });
      toast.success('Usuário criado com sucesso!');
    },
    onError: (error: any) => {
      toast.error('Erro ao criar usuário: ' + error.message);
    },
  });

  // Mutation para atualizar usuário
  // NOTA: a atualização é exclusivamente da tabela `usuarios`. Credencial não é
  // alterada aqui — por isso o tipo exclui `senha`, impedindo que alguém volte a
  // enviar esse campo esperando que a senha mude. A troca de senha é feita pelo
  // fluxo de e-mail (/esqueceu-senha -> /redefinir-senha).
  const { mutateAsync: atualizarUsuario } = useMutation({
    mutationFn: async ({ id, usuario }: { id: string; usuario: Partial<Omit<UsuarioInput, 'senha'>> }) => {
      const { error } = await supabase
        .from('usuarios')
        .update({
          nome: usuario.nome,
          tipo: usuario.tipo,
          documento: usuario.documento,
          cep: usuario.cep,
          endereco: usuario.endereco,
          bairro: usuario.bairro,
          cidade: usuario.cidade,
          uf: usuario.uf,
          telefone: usuario.telefone,
          observacao: usuario.observacao,
          id_revenda: usuario.id_revenda,
          nome_revenda: usuario.nome_revenda,
          ativo: usuario.ativo,
          // Contato de suporte: só é tocado quando o tipo vem no payload (salvamento do
          // formulário completo). Em updates parciais (saldo, bloqueio) as chaves ficam
          // undefined e são descartadas, preservando a configuração da revenda.
          ...(usuario.tipo === 'revenda'
            ? {
                celular: usuario.celular,
                mostra_botao: usuario.mostra_botao ?? false,
              }
            : usuario.tipo
              ? { celular: null, mostra_botao: false }
              : {}),
          // Altura da impressão térmica: só o admin grava (o trigger de blindagem recusa os
          // demais). Mesma regra do contato: só no salvamento do formulário, nunca em updates
          // parciais, e só quando o formulário trouxe o valor.
          ...(user?.tipo === 'admin' && usuario.tipo && usuario.ajusta_altura_termica !== undefined
            ? {
                ajusta_altura_termica: usuario.ajusta_altura_termica,
                altura_termica_ajuste: usuario.altura_termica_ajuste ?? 0,
              }
            : {}),
        })
        .eq('id', id);

      if (error) {
        console.error('[useUsuarios] atualizarUsuario: Erro na atualização:', error);
        throw error;
      }
      
      return true;
    },
    onSuccess: (_, variables) => {
      // Invalidar queries específicas e gerais
      queryClient.invalidateQueries({ queryKey: ['usuarios'] });
      queryClient.invalidateQueries({ queryKey: ['usuarios', variables.id] });
      
      // Se estamos atualizando o usuário logado, pode ser necessário refrescar dados específicos
      if (user && variables.id === user.id) {
        // Forçar revalidação de queries relacionadas ao usuário atual
        queryClient.invalidateQueries({ queryKey: ['usuarios', user.tipo, user.id] });
      }
      
      toast.success('Usuário atualizado com sucesso!');
    },
    onError: (error: any) => {
      console.error('[useUsuarios] atualizarUsuario: Erro:', error);
      toast.error('Erro ao atualizar usuário: ' + error.message);
    },
  });

  // Adicionar função para forçar recarregamento dos dados
  const recarregarUsuarios = () => {
    return queryClient.invalidateQueries({ queryKey: ['usuarios'] });
  };

  return {
    usuarios,
    listLoading,
    useUsuario,
    criarUsuario,
    atualizarUsuario,
    recarregarUsuarios, // Exportar a nova função
  };
} 